import { Pill } from "./cells";
import { SortHeading, sortItems, useTableSort } from "./sorting";

/**
 * Raph's retention table: target, forecast, and where we actually are.
 *
 *   Retention target | Forecast | Current number
 *
 * Target is last year's closing number, typed in. Forecast is what we expect to
 * be left at year end — the cohort less those churned, forecast not to renew,
 * or listed unlikely. Current is how many of that cohort are still live today.
 *
 * Forecast and current are shown against the target rather than in isolation,
 * because the question the board asks is whether we are on track, and a bare
 * count does not answer it.
 */
function Delta({ value, target }) {
  if (target === null || target === undefined || value === null || value === undefined) {
    return <span className="text-[#a9b3bb]">—</span>;
  }
  const difference = value - target;
  if (difference === 0) return <span className="text-[#7d8b96]">on target</span>;
  return (
    <span className={difference > 0 ? "text-emerald-600" : "text-red-600"}>
      {difference > 0 ? "+" : ""}
      {new Intl.NumberFormat("en-GB").format(difference)}
    </span>
  );
}

export default function CohortTable({
  groups: unsorted = [],
  measure = "units",
  totals,
  onGroupClick,
  savedSort,
  onSortChange,
}) {
  const [sort, toggleSort] = useTableSort(savedSort, onSortChange);
  if (!unsorted.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records in this cohort.</p>;
  }

  const isMoney = measure !== "units";
  const format = (value) =>
    value === null || value === undefined
      ? "—"
      : isMoney
        ? new Intl.NumberFormat("en-GB", {
            style: "currency", currency: "GBP", maximumFractionDigits: 0,
          }).format(value)
        : new Intl.NumberFormat("en-GB").format(value);

  // Every column reads in the same units as the measure. A revenue report
  // showing a pound figure beside a headcount in the same row is unreadable,
  // and reads as a contradiction when the two disagree.
  // Said explicitly: forecast and live today answer different questions. One
  // is a projection to year end, the other the position today, and without
  // the labels a forecast below the current number looks like an error.
  const headings = [
    { column: "label", label: "Product", get: (g) => g.label },
    {
      column: "cohort",
      label: isMoney ? "Opening value" : "Opening number",
      get: (g) => (isMoney ? g.cohort : g.cohort_count),
      numeric: true,
    },
    { column: "target", label: "Retention target", get: (g) => g.target, numeric: true },
    {
      column: "forecast",
      label: "Forecast at year end",
      get: (g) => (isMoney ? g.forecast_remaining_value : g.forecast_remaining),
      numeric: true,
    },
    {
      column: "unlikely",
      label: "Unlikely to renew",
      get: (g) => (isMoney ? g.unlikely_value : g.unlikely_count),
      numeric: true,
    },
    {
      column: "retained",
      label: "Live today",
      get: (g) => (isMoney ? g.retained : g.retained_count),
      numeric: true,
    },
    {
      column: "delta",
      label: "vs target",
      get: (g) => (g.target === null || g.target === undefined ? null : g.retained_count - g.target),
      numeric: true,
    },
  ];
  const active = headings.find((h) => h.column === sort?.column);
  const groups = active ? sortItems(unsorted, active.get, sort.direction) : unsorted;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#cccccc]">
            {headings.map((heading, index) => (
              <SortHeading
                key={heading.column}
                label={heading.label}
                column={heading.column}
                sort={sort}
                onToggle={toggleSort}
                numeric={heading.numeric}
                align={index === 0 ? "text-left" : "text-right"}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr
              key={group.key || group.label}
              onClick={() => onGroupClick && onGroupClick(group.label)}
              className={`border-b border-[#efefef] last:border-0 ${
                onGroupClick ? "cursor-pointer hover:bg-[#f5f5f5]" : ""
              }`}
            >
              <td className="px-3 py-2.5 font-medium text-[#323c44]">{group.label}</td>
              <td className="px-3 py-2.5 text-right">
                <Pill tone={isMoney ? "money" : "neutral"}>
                  {isMoney ? format(group.cohort) : group.cohort_count}
                </Pill>
              </td>
              <td className="px-3 py-2.5 text-right">
                {group.target === null || group.target === undefined ? (
                  <span className="text-[#a9b3bb]">—</span>
                ) : (
                  <Pill>{group.target}</Pill>
                )}
              </td>
              <td className="px-3 py-2.5 text-right">
                <Pill>
                  {isMoney ? format(group.forecast_remaining_value) : group.forecast_remaining}
                </Pill>
                {group.forecast_loss > 0 && (
                  // The losses behind the forecast, so the number is checkable
                  // without opening the drilldown.
                  <span className="ml-1.5 text-[11px] text-[#a9b3bb]">
                    {/* The client count beside the money, so a revenue row
                        can be read against the unit report's forecast. */}
                    {isMoney
                      ? `(−${format(group.forecast_loss_value)}, ${group.forecast_loss} client${group.forecast_loss === 1 ? "" : "s"})`
                      : `(−${group.forecast_loss})`}
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5 text-right">
                {group.unlikely_count > 0 ? (
                  <Pill tone="warn">
                    {isMoney ? format(group.unlikely_value) : group.unlikely_count}
                  </Pill>
                ) : (
                  <span className="text-[#a9b3bb]">—</span>
                )}
              </td>
              <td className="px-3 py-2.5 text-right">
                <Pill
                  tone={
                    isMoney || group.target === null || group.target === undefined
                      ? "neutral"
                      : group.retained_count >= group.target
                        ? "good"
                        : "warn"
                  }
                >
                  {isMoney ? format(group.retained) : group.retained_count}
                </Pill>
                {group.rate !== null && group.rate !== undefined && (
                  <span className="ml-1.5 text-[11px] text-[#7d8b96]">{group.rate}%</span>
                )}
              </td>
              <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                {/* A target is a count of services, so it is only comparable
                    against the count, not against a value. */}
                {isMoney ? (
                  <span className="text-[#a9b3bb]">—</span>
                ) : (
                  <Delta value={group.retained_count} target={group.target} />
                )}
              </td>
            </tr>
          ))}
          {totals && (
            <tr className="border-t-2 border-[#cccccc] font-semibold">
              <td className="px-3 py-2.5 text-[#323c44]">Total</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#323c44]">
                {isMoney ? format(totals.cohort_value) : totals.cohort_count}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#4a5b68]">
                {groups.every((g) => g.target === null || g.target === undefined)
                  ? "—"
                  : groups.reduce((sum, g) => sum + (g.target || 0), 0)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#323c44]">
                {isMoney
                  ? format(groups.reduce((sum, g) => sum + (g.forecast_remaining_value || 0), 0))
                  : groups.reduce((sum, g) => sum + (g.forecast_remaining || 0), 0)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#323c44]">
                {isMoney
                  ? format(groups.reduce((sum, g) => sum + (g.unlikely_value || 0), 0))
                  : groups.reduce((sum, g) => sum + (g.unlikely_count || 0), 0)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#323c44]">
                {isMoney ? format(totals.retained_value) : totals.retained_count}
              </td>
              <td className="px-3 py-2.5" />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
